import React, {useEffect, useState} from 'react'
import {useParams, Link} from 'react-router-dom'
import Button from 'react-bootstrap/Button'
import Select from 'react-select'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faArrowLeft, faEdit, faTrash, faUserPlus, faSave} from '@fortawesome/free-solid-svg-icons'

import {get, put, del} from '../utils/fetchAPI'
import {Toast, swalDel} from '../utils/SwalUti'
import PatientFormModal from '../components/PatientFormModal'

function AccountDetail() {
    const {accountId} = useParams()
    const [account, setAccount] = useState(null)
    const [patients, setPatients] = useState([])
    const [doctorList, setDoctorList] = useState([])
    const [loading, setLoading] = useState(true)
    const [editingAccount, setEditingAccount] = useState(false)
    const [accountForm, setAccountForm] = useState({})
    const [savingAccount, setSavingAccount] = useState(false)

    const [modalShow, setModalShow] = useState(false)
    const [editingPatient, setEditingPatient] = useState(null)

    const loadAll = async () => {
        setLoading(true)
        try {
            const [accountRes, patientsRes] = await Promise.all([
                get(`/user/${accountId}`),
                get(`/user/${accountId}/patients`),
            ])
            setAccount(accountRes.data)
            setAccountForm({
                first: accountRes.data.first || '',
                last: accountRes.data.last || '',
                email: accountRes.data.email || '',
                status: accountRes.data.status || '',
                doctors: accountRes.data.doctors || [],
            })
            // Sort: self first, then by createdAt
            const sorted = [...patientsRes.data].sort((a, b) => {
                if (a.isSelf && !b.isSelf) return -1
                if (!a.isSelf && b.isSelf) return 1
                return new Date(a.createdAt) - new Date(b.createdAt)
            })
            setPatients(sorted)
        } catch (e) {
            Toast.fire({icon: 'error', title: 'Failed to load account'})
        } finally {
            setLoading(false)
        }
    }

    const loadDoctors = async () => {
        try {
            const hcpId = account?.hcp?._id || account?.hcp
            if (!hcpId) return
            const res = await get(`/hcp/${hcpId}/doctors`)
            setDoctorList(res.data)
        } catch (e) {
            console.log(e)
        }
    }

    useEffect(() => { loadAll() }, [accountId])
    useEffect(() => { if (account) loadDoctors() }, [account])

    const handleAccountField = (e) => {
        setAccountForm({...accountForm, [e.target.name]: e.target.value})
    }

    const handleAccountDoctors = (data) => {
        setAccountForm({...accountForm, doctors: data})
    }

    const saveAccount = async (e) => {
        e.preventDefault()
        setSavingAccount(true)
        try {
            const payload = {
                first: accountForm.first,
                last: accountForm.last,
                email: accountForm.email,
                status: accountForm.status,
                doctors: (accountForm.doctors || []).map(d => d._id || d),
            }
            await put(`/user/${accountId}`, payload)
            Toast.fire({icon: 'success', title: 'Account updated'})
            setEditingAccount(false)
            loadAll()
        } catch (err) {
            Toast.fire({icon: 'error', title: err?.response?.data?.message || 'Update failed'})
        } finally {
            setSavingAccount(false)
        }
    }

    const openAddPatient = () => {
        setEditingPatient(null)
        setModalShow(true)
    }

    const openEditPatient = (p) => {
        setEditingPatient(p)
        setModalShow(true)
    }

    const handleDeletePatient = (p) => {
        if (p.isSelf) return
        swalDel('This patient record will be deleted permanently!').then(result => {
            if (!result.value) return
            del(`/patients/${p._id}`)
                .then(() => {
                    Toast.fire({icon: 'success', title: 'Deleted'})
                    loadAll()
                })
                .catch(err => Toast.fire({icon: 'error', title: err?.response?.data?.message || 'Delete failed'}))
        })
    }

    // The shared modal handles save itself; we override its onSaved to refresh.
    // For new patients we need to inject `accountId` into the payload so the
    // backend creates the Patient under the right account.
    const handleSavedFromModal = () => {
        loadAll()
    }

    if (loading) {
        return (
            <div className="content-wrapper">
                <section className="content pt-3">
                    <div className="container-fluid"><div className="loader"/></div>
                </section>
            </div>
        )
    }

    if (!account) {
        return (
            <div className="content-wrapper">
                <section className="content pt-3">
                    <div className="container-fluid"><p>Account not found.</p></div>
                </section>
            </div>
        )
    }

    return (
        <div className="content-wrapper">
            <section className="content pt-3">
                <div className="container-fluid">
                    <div className="mb-2">
                        <Link to="/user"><FontAwesomeIcon icon={faArrowLeft}/> Back to Users</Link>
                    </div>

                    {/* Account info card */}
                    <div className="card">
                        <div className="card-header">
                            <h3 className="card-title pt-1">Account</h3>
                            <div className="float-right">
                                {!editingAccount ? (
                                    <Button variant="link" onClick={() => setEditingAccount(true)}>
                                        <FontAwesomeIcon icon={faEdit}/> Edit
                                    </Button>
                                ) : (
                                    <Button variant="link" onClick={() => { setEditingAccount(false); loadAll() }}>
                                        Cancel
                                    </Button>
                                )}
                            </div>
                        </div>
                        <div className="card-body">
                            {!editingAccount ? (
                                <div className="row">
                                    <div className="col-md-4"><strong>Name:</strong> {account.first} {account.last}</div>
                                    <div className="col-md-4"><strong>Email:</strong> {account.email}</div>
                                    <div className="col-md-4"><strong>Status:</strong> {account.status}</div>
                                    <div className="col-md-12 mt-2">
                                        <strong>Assigned Doctors:</strong>{' '}
                                        {(account.doctors || []).map(d => `${d.first} ${d.last}`).join(', ') || '—'}
                                    </div>
                                </div>
                            ) : (
                                <form onSubmit={saveAccount}>
                                    <div className="form-row">
                                        <div className="form-group col-md-4 px-2">
                                            <label>First Name</label>
                                            <input type="text" name="first" className="form-control"
                                                   value={accountForm.first} onChange={handleAccountField}/>
                                        </div>
                                        <div className="form-group col-md-4 px-2">
                                            <label>Last Name</label>
                                            <input type="text" name="last" className="form-control"
                                                   value={accountForm.last} onChange={handleAccountField}/>
                                        </div>
                                        <div className="form-group col-md-4 px-2">
                                            <label>Status</label>
                                            <select name="status" className="form-control"
                                                    value={accountForm.status} onChange={handleAccountField}>
                                                <option value="registered">Registered</option>
                                                <option value="invited">Invited</option>
                                                <option value="created">Created</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="form-row">
                                        <div className="form-group col-md-6 px-2">
                                            <label>Email</label>
                                            <input type="email" name="email" className="form-control"
                                                   value={accountForm.email} onChange={handleAccountField}/>
                                        </div>
                                        <div className="form-group col-md-6 px-2">
                                            <label>Assigned Doctors</label>
                                            <Select
                                                isMulti
                                                options={doctorList}
                                                value={accountForm.doctors}
                                                getOptionLabel={o => `${o.first} ${o.last}`}
                                                getOptionValue={o => o._id}
                                                onChange={handleAccountDoctors}
                                                className="basic-multi-select"
                                                classNamePrefix="select"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <Button type="submit" variant="primary" disabled={savingAccount}>
                                            <FontAwesomeIcon icon={faSave}/> {savingAccount ? 'Saving…' : 'Save'}
                                        </Button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>

                    {/* Patients table */}
                    <div className="card mt-3">
                        <div className="card-header">
                            <h3 className="card-title pt-1">Patients</h3>
                            <a href='' onClick={(e) => { e.preventDefault(); openAddPatient() }}
                               className="ml-4 card-title">
                                <FontAwesomeIcon icon={faUserPlus}/> Add Family Member
                            </a>
                        </div>
                        <div className="card-body" style={{overflowX: 'auto'}}>
                            <table className="table table-hover table-sm">
                                <thead>
                                <tr>
                                    <th>NAME</th>
                                    <th>RELATIONSHIP</th>
                                    <th>DOB</th>
                                    <th>SEX</th>
                                    <th>PHONE</th>
                                    <th>INSURANCE</th>
                                    <th className="text-center">ACTIONS</th>
                                </tr>
                                </thead>
                                <tbody>
                                {patients.length === 0 && (
                                    <tr><td colSpan="7" className="text-center text-muted">No patients yet.</td></tr>
                                )}
                                {patients.map(p => (
                                    <tr key={p._id}>
                                        <td>{p.first} {p.last}</td>
                                        <td>{p.relationship}{p.isSelf ? ' (self)' : ''}</td>
                                        <td>{p.dateOfBirth ? new Date(p.dateOfBirth).toLocaleDateString() : '—'}</td>
                                        <td>{p.sex || '—'}</td>
                                        <td>{p.mobile || '—'}</td>
                                        <td>{p.insurance || '—'}</td>
                                        <td className="text-center">
                                            <div className="btn-group btn-group-sm">
                                                <button type="button" className="btn btn-sm"
                                                        onClick={() => openEditPatient(p)}>
                                                    <FontAwesomeIcon icon={faEdit} className='text-green'/>
                                                </button>
                                                <button type="button" className="btn btn-sm"
                                                        disabled={p.isSelf}
                                                        onClick={() => handleDeletePatient(p)}>
                                                    <FontAwesomeIcon icon={faTrash}
                                                                     className={p.isSelf ? 'text-muted' : 'text-red'}/>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <PatientFormModal
                        show={modalShow}
                        onHide={() => setModalShow(false)}
                        onSaved={handleSavedFromModal}
                        initialPatient={editingPatient}
                        accountId={accountId}
                    />
                </div>
            </section>
        </div>
    )
}

export default AccountDetail
